import React from 'react'
import {Elements} from '@recurly/react-recurly'
import Icon from '../../core/shared/Icon'
import {Tooltip} from '../../core/shared/Tooltip'
import {Input} from '../../core/shared/Input'
import {Select} from '../../core/shared/Select'
import rawCountryStates from 'iso3166-2-db/i18n/en'
import {TaxesPromptContainer} from '../ui/payment-information'
import {useUserFeatures} from '../../context/user-features-context'
import {AutopayTerms} from './autopay-terms'
import {usePaymentAddress} from './contexts/payment-address-context'
import {usePaymentCheckboxes} from './contexts/payment-checkboxes-context'
import {usePaymentBankAccount} from './contexts/payment-bank-account-context'
// import {useViewport} from '../../utils/useViewport'
import {useRecurlyPayment} from './contexts/recurly-payment-context'
import {COUNTRIES} from './constants'
import {usePaymentOrder} from './hooks/use-payment-order'
import {useSelector} from 'react-redux'
import {selectedMobileViewEnabledStatus} from '../../app-store/kpGlobalStateSlice'

const ACCOUNT_TYPE_OPTIONS = [
  {label: 'Checking', value: 'checking'},
  {label: 'Savings', value: 'savings'},
]
const AchRecurlyPaymentForm = ({
  showFirstSection,
  showSecondSection,
  nextStep,
  businessAddress,
  country,
  bizphone,
  isAddressUpdated,
  setCurrentStep,
  disclaimerText,
}) => {
  const isMobile = useSelector(selectedMobileViewEnabledStatus)
  const [selectedCountry, setSelectedCountry] = React.useState(
    COUNTRIES.find(({value}) => value === country),
  )
  const countrySelectDisabled = true
  const {
    addressInformation,
    updateAddressField,
    resetAddress,
    setFromBusinessAddress,
  } = usePaymentAddress()
  const {checkBoxesValues, setCheckBoxesValues} = usePaymentCheckboxes()
  const {fetchBankAccountToken, recurlyToken, type} = useRecurlyPayment()
  const {is5Order} = usePaymentOrder()
  const {bankAccountInformation, updateBankAccountField} =
    usePaymentBankAccount()
  const [listOfStates, setListOfStates] = React.useState([])
  const formRef = React.useRef()
  const {useBusinessAddressInfo} = checkBoxesValues
  const {Updated_Terms: frontendUpdatedTerms} = useUserFeatures()

  React.useEffect(() => {
    setSelectedCountry(COUNTRIES.find(({value}) => value === country))
  }, [country])

  const handleOnchange = ({target: {value, name}}) => {
    console.log('handle on change')
    updateAddressField(name, value)
  }

  const handleBankAccountChange = ({target: {value, name}}) => {
    updateBankAccountField(name, value)
  }

  React.useEffect(() => {
    const getStates = () => {
      let stateLabel = ''
      if (['US', 'AU', 'NZ'].includes(selectedCountry.value)) {
        stateLabel = 'State'
      } else if (selectedCountry.value === 'KY') {
        stateLabel = 'City'
      } else if (selectedCountry.value === 'CA') {
        stateLabel = 'Province'
      } else if (selectedCountry.value === 'BB') {
        stateLabel = 'Parish'
      }
      let blankOption = [{name: 'Select ' + stateLabel, value: ''}]
      const states = rawCountryStates[selectedCountry.value].regions
        .map(({iso, name}) => ({
          name,
          value: iso,
        }))
        .sort((a, b) => (a.name > b.name ? 1 : -1))
      setListOfStates([...blankOption, ...states])
    }
    if (selectedCountry.value) {
      updateBankAccountField('country', selectedCountry.value)
      getStates(selectedCountry.value)
    }
  }, [selectedCountry])

  React.useEffect(() => {
    if (type !== 'ach') return undefined
    if (useBusinessAddressInfo) {
      if (checkBoxesValues.updateBillingAddress) {
        setCheckBoxesValues({...checkBoxesValues, updateBillingAddress: false})
      }
      setFromBusinessAddress(businessAddress)
    } else {
      if (isMobile) {
        updateAddressField('address', '')
        updateAddressField('city', '')
        updateAddressField('firstName', '')
        updateAddressField('lastName', '')
        updateAddressField('state', '')
      } else {
        resetAddress()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBusinessAddressInfo, businessAddress, listOfStates, isMobile, type])

  React.useEffect(() => {
    if (type !== 'ach') return null
    if (!checkBoxesValues.agreedTerms) return null
    if (recurlyToken) return null
    fetchBankAccountToken(formRef.current).catch(() => {
      setCurrentStep?.(2)
      setCheckBoxesValues({
        ...checkBoxesValues,
        agreedTerms: false,
      })
    })
  }, [recurlyToken, checkBoxesValues.agreedTerms, type])

  return (
    <form
      ref={formRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      {/* BANK ACCOUNT DETAILS */}
      <div
        style={{
          background: 'white',
          borderRadius: '10px',
          padding: isMobile ? '10px' : '16px',
        }}
      >
        <div className="section-heading">
          <div className="section-heading-title-container">
            <div
              className="d-flex align-items-center"
              style={{columnGap: '8px'}}
            >
              <Tooltip
                variant="bottom"
                description="Provide bank account details to proceed with payment"
                width="220px"
              >
                <div
                  className="d-flex align-items-center"
                  style={{columnGap: '4px'}}
                >
                  <p className="section-heading-title">
                    Enter Bank Account Details
                  </p>
                  <Icon name="info" color="#808080" size="xxs" />
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
        <div
          className="d-flex justify-content-between"
          style={{
            gap: isMobile ? '10px' : '36px',
            marginBottom: isMobile ? '10px' : '20px',
          }}
        >
          <Input
            variant="payment"
            label="First Name"
            data-recurly="first_name"
            placeholder="First Name"
            name="firstName"
            onChange={handleOnchange}
            value={addressInformation.firstName}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          />
          <Input
            variant="payment"
            label="Last Name"
            data-recurly="last_name"
            placeholder="Last Name"
            name="lastName"
            onChange={handleOnchange}
            value={addressInformation.lastName}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          />
        </div>
        <Input
          variant="payment"
          label="Company Name"
          data-recurly="business_name"
          placeholder="Company Name"
          name="company"
          onChange={handleOnchange}
          value={addressInformation.company}
          disabled={checkBoxesValues.agreedTerms}
          title={
            checkBoxesValues.agreedTerms
              ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
              : undefined
          }
          style={{marginBottom: isMobile ? '10px' : '20px'}}
        />
        <Input
          variant="payment"
          label="Phone Number"
          data-recurly="business_phone"
          placeholder="Phone Number"
          name="phone"
          onChange={handleOnchange}
          value={addressInformation.phone}
          disabled={checkBoxesValues.agreedTerms}
          title={
            checkBoxesValues.agreedTerms
              ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
              : undefined
          }
          style={{marginBottom: isMobile ? '10px' : '20px'}}
        />
        <Input
          variant="payment"
          label="Account Name"
          data-recurly="name_on_account"
          placeholder="Account Name"
          name="accountName"
          onChange={handleBankAccountChange}
          value={bankAccountInformation.accountName}
          disabled={checkBoxesValues.agreedTerms}
          title={
            checkBoxesValues.agreedTerms
              ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
              : undefined
          }
          style={{marginBottom: isMobile ? '10px' : '20px'}}
        />
        <Input
          variant="payment"
          label="Routing Number"
          data-recurly="routing_number"
          data-dd-privacy="mask"
          placeholder="Routing Number"
          name="routingNumber"
          onChange={handleBankAccountChange}
          value={bankAccountInformation.routingNumber}
          disabled={checkBoxesValues.agreedTerms}
          title={
            checkBoxesValues.agreedTerms
              ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
              : undefined
          }
          style={{marginBottom: isMobile ? '10px' : '20px'}}
        />
        <div
          className="d-flex justify-content-between"
          style={{
            gap: isMobile ? '10px' : '36px',
            marginBottom: isMobile ? '10px' : '20px',
          }}
        >
          <Input
            variant="payment"
            label="Account Number"
            data-recurly="account_number"
            data-dd-privacy="mask"
            placeholder="Account Number"
            name="accountNumber"
            onChange={handleBankAccountChange}
            value={bankAccountInformation.accountNumber}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          />
          <Input
            variant="payment"
            label="Confirm Account Number"
            data-recurly="account_number_confirmation"
            data-dd-privacy="mask"
            placeholder="Account Number Confirmation"
            name="accountNumberConfirmation"
            onChange={handleBankAccountChange}
            value={bankAccountInformation.accountNumberConfirmation}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          />
          {bankAccountInformation.accountNumberConfirmation.length > 0 &&
            bankAccountInformation.accountNumber !==
              bankAccountInformation.accountNumberConfirmation && (
              <p style={{color: 'red', fontSize: '12px', marginTop: '4px'}}>
                Account numbers do not match.
              </p>
            )}
        </div>
        <div>
          <label className="payment-variant-label">Banking Account Type</label>
          <div className="d-flex gap-3 mt-2">
            {ACCOUNT_TYPE_OPTIONS.map(item => (
              <div
                key={item.label}
                style={{display: 'flex', alignItems: 'center', gap: '6px'}}
              >
                <input
                  type="radio"
                  id={item.value}
                  name="accountType"
                  data-recurly="account_type"
                  data-dd-privacy="mask"
                  value={item.value}
                  checked={bankAccountInformation.accountType === item.value}
                  disabled={checkBoxesValues.agreedTerms}
                  onChange={() => {
                    console.log(item.value)
                    handleBankAccountChange({
                      target: {name: 'accountType', value: item.value},
                    })
                  }}
                  style={{
                    margin: 0,
                    cursor: 'pointer',
                    accentColor: '#5378FC',
                  }}
                />
                <label
                  htmlFor={item.value}
                  style={{
                    fontFamily: 'Open Sans',
                    fontSize: '12px',
                    fontWeight: 400,
                    color: '#4D4D4D',
                    marginBottom: 0,
                    cursor: 'pointer',
                  }}
                >
                  {item.label}
                </label>
              </div>
            ))}
          </div>
        </div>
        {is5Order && frontendUpdatedTerms && <AutopayTerms />}
      </div>

      {/* BILLING ADDRESS */}
      <div
        style={{
          background: 'white',
          borderRadius: '10px',
          padding: isMobile ? '10px' : '16px',
        }}
      >
        {is5Order && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div className="section-heading section-heading--no-divider">
              <div className="section-heading-title-container">
                <div
                  className="d-flex align-items-center"
                  style={{columnGap: '16px'}}
                >
                  <Tooltip
                    variant="top"
                    description="Provide the billing address for this payment"
                    width="220px"
                  >
                    <div
                      className="d-flex align-items-center"
                      style={{columnGap: '4px'}}
                    >
                      <p className="section-heading-title">Billing Address</p>
                      <Icon name="info" color="#808080" size="xxs" />
                    </div>
                  </Tooltip>
                  <div
                    className="d-flex align-items-center"
                    style={{columnGap: '6px'}}
                  >
                    <input
                      type="checkbox"
                      id="useBusinessAddressInfoAch"
                      name="useBusinessAddressInfo"
                      disabled={checkBoxesValues.agreedTerms}
                      checked={checkBoxesValues.useBusinessAddressInfo}
                      onChange={e => {
                        setCheckBoxesValues({
                          ...checkBoxesValues,
                          useBusinessAddressInfo: e.target.checked,
                        })
                      }}
                      style={{
                        cursor: 'pointer',
                        width: '16px',
                        height: '16px',
                        accentColor: '#5378FC',
                      }}
                      title={
                        checkBoxesValues.agreedTerms
                          ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                          : undefined
                      }
                    />
                    <label
                      htmlFor="useBusinessAddressInfoAch"
                      className="business-address-label"
                      style={{
                        cursor: 'pointer',
                        marginBottom: '0px',
                      }}
                    >
                      Same as my business address
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {is5Order && isAddressUpdated && (
          <TaxesPromptContainer>
            <Icon name="info" color="#057AFF" />
            <p className="m-0">
              The current estimated taxes are based on your business address.
            </p>
          </TaxesPromptContainer>
        )}
        {!is5Order && (
          <Select
            options={COUNTRIES.map(({name, value}) => ({label: name, value}))}
            label="Country"
            className="recurly-element recurly-address mt-3"
            data-recurly="country"
            value={selectedCountry?.value}
            onChange={e =>
              setSelectedCountry(
                COUNTRIES.find(c => c.value === e.target.value),
              )
            }
            disabled={countrySelectDisabled}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          />
        )}
        <input
          hidden
          readOnly
          type="text"
          placeholder="Country"
          value={selectedCountry.value}
          style={{
            fontSize: '18px',
          }}
          className="recurly-element recurly-address ml-2"
          data-recurly="country"
        />
        <Input
          variant="payment"
          label="Street Address"
          type="text"
          data-recurly="address1"
          placeholder="Street Address"
          name="address"
          value={addressInformation.address}
          onChange={handleOnchange}
          disabled={checkBoxesValues.agreedTerms}
          title={
            checkBoxesValues.agreedTerms
              ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
              : undefined
          }
          style={{
            marginBottom: '20px',
          }}
        />
        <div
          className={`responsive-address-container ${
            !is5Order && 'pr-1 address_container'
          }`}
          style={{marginBottom: isMobile ? '10px' : '20px'}}
        >
          <Input
            variant="payment"
            label="City"
            type="text"
            data-recurly="city"
            placeholder="City"
            name="city"
            value={addressInformation.city}
            onChange={handleOnchange}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          />
          <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
            <label className="payment-variant-label">State</label>
            <select
              className="recurly-address"
              style={{
                height: '32px',
                border: '1px solid #BFBFBF',
                borderRadius: '5px',
                fontFamily: 'Open Sans',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '100%',
                letterSpacing: '0%',
                paddingLeft: '8px',
              }}
              value={addressInformation?.state}
              onChange={e => {
                handleOnchange({
                  target: {
                    name: 'state',
                    value: e.target.selectedOptions[0].value,
                  },
                })
              }}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                  : undefined
              }
            >
              {listOfStates.map(({name, value}) => {
                return (
                  <option key={value} value={value}>
                    {name}
                  </option>
                )
              })}
            </select>
          </div>
          <input
            hidden
            readOnly
            value={addressInformation?.state}
            type="text"
            className="recurly-element"
            data-recurly="state"
            placeholder="State"
          />
          <Input
            variant="payment"
            label="Postal Code"
            type="text"
            data-recurly="postal_code"
            placeholder="Postal Code"
            name="postalCode"
            value={addressInformation.postalCode}
            onChange={handleOnchange}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          />
        </div>
        {is5Order && (
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : undefined,
              gap: isMobile ? '10px' : '16px',
              alignItems: isMobile ? 'stretch' : 'flex-start',
            }}
          >
            <div style={{flex: 1}}>
              <Select
                options={COUNTRIES.map(({name, value}) => ({
                  label: name,
                  value,
                }))}
                label="Country"
                size="sm"
                variant="payment"
                className="payment-country-select"
                data-recurly="country"
                value={selectedCountry?.value}
                onChange={e =>
                  setSelectedCountry(
                    COUNTRIES.find(c => c.value === e.target.value),
                  )
                }
                disabled={countrySelectDisabled}
                title={
                  checkBoxesValues.agreedTerms
                    ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                    : undefined
                }
              />
            </div>
            <div
              style={{
                flex: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                paddingTop: isMobile ? '10px' : '16px',
              }}
            >
              {disclaimerText && (
                <p className="card-number-helper-text" style={{margin: 0}}>
                  {disclaimerText}
                </p>
              )}
              <p className="card-number-helper-text" style={{margin: 0}}>
                {
                  "By processing your payment, you agree that your personal information will be shared with Thryv's payment processor—Adyen, N.V. and will be subject to Adyen's "
                }
                <a
                  target="_blank"
                  href="https://www.adyen.com/policies-and-disclaimer/privacy-policy"
                  rel="noreferrer"
                  style={{fontWeight: 700, color: '#5378FC'}}
                >
                  privacy statement
                </a>
                {'.'}
              </p>
            </div>
          </div>
        )}
        {!is5Order && (
          <div className="mt-4 mb-2">
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <input
                type="checkbox"
                id="useBusinessAddressInfoDesktop2"
                name="useBusinessAddressInfo"
                checked={checkBoxesValues.useBusinessAddressInfo}
                disabled={checkBoxesValues.agreedTerms}
                onChange={e => {
                  setCheckBoxesValues({
                    ...checkBoxesValues,
                    useBusinessAddressInfo: e.currentTarget.checked,
                  })
                }}
                title={
                  checkBoxesValues.agreedTerms
                    ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                    : undefined
                }
                style={{margin: 0}}
              />
              <label
                htmlFor="useBusinessAddressInfoDesktop2"
                style={{marginBottom: 0}}
              >
                Same as my business address
              </label>
            </div>
            {frontendUpdatedTerms && <AutopayTerms styles={{marginTop: 0}} />}
          </div>
        )}
      </div>
    </form>
  )
}

const AchPaymentForm = ({
  nextStep,
  calculateTaxes,
  calculatingTaxes,
  setCalculatingTaxes,
  showFirstSection = false,
  showSecondSection = false,
  checkBoxesValues,
  setCheckBoxesValues,
  setName,
  businessAddress,
  country,
  handleCompleteOrder,
  setIsAddressUpdated,
  bizphone,
  is5Order,
  isAddressUpdated,
  paymentPreview,
  setCurrentStep,
  disclaimerText,
}) => {
  return (
    <Elements>
      <AchRecurlyPaymentForm
        saveRecurlyToken={calculateTaxes}
        calculatingTaxes={calculatingTaxes}
        setCalculatingTaxes={setCalculatingTaxes}
        showFirstSection={showFirstSection}
        showSecondSection={showSecondSection}
        nextStep={nextStep}
        checkBoxesValues={checkBoxesValues}
        setCheckBoxesValues={setCheckBoxesValues}
        setName={setName}
        businessAddress={businessAddress}
        country={country}
        handleCompleteOrder={handleCompleteOrder}
        setIsAddressUpdated={setIsAddressUpdated}
        bizphone={bizphone}
        is5Order={is5Order}
        isAddressUpdated={isAddressUpdated}
        paymentPreview={paymentPreview}
        setCurrentStep={setCurrentStep}
        disclaimerText={disclaimerText}
      />
    </Elements>
  )
}

export {AchPaymentForm}
